import { createRef, PureComponent } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { noop, omit, keys, isObject } from 'lodash/fp';
import mapProps from 'map-props';
import InputRange from 'react-input-range';
import elementType from 'prop-types-extra/lib/elementType';
import cns from 'classnames';

import DefaultRangeLabel from './DefaultRangeLabel';
import DefaultInputControl from './DefaultInputControl';
import propsMapping from './propsMapping';

// https://github.com/davidchin/react-input-range/issues/129
import './styles.global.less';
import cn from './styles.local.less';

export const propTypes = {
  RangeLabel: elementType,
  InputControl: elementType,
  className: PropTypes.string,
  input: PropTypes.object.isRequired,
  options: PropTypes.array,
  isRangeMode: PropTypes.bool,
  inputRangeProps: PropTypes.object,
  valuesByIndex: PropTypes.object,
  indexesByValue: PropTypes.object,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  onChanged: PropTypes.func,
  onInputControlChange: PropTypes.func,
  onSliderChange: PropTypes.func,
};

const getOtherProps = omit(keys(propTypes));

class RangeSliderControl extends PureComponent {
  static propTypes = propTypes;

  static defaultProps = {
    RangeLabel: DefaultRangeLabel,
    InputControl: DefaultInputControl,
    onChanged: noop,
    onInputControlChange: noop,
    onSliderChange: noop,
  };

  constructor(props) {
    super(props);
    this.sliderRef = createRef();
  }

  state = null;

  UNSAFE_componentWillMount() {
    const { input } = this.props;

    this.setFormattedValue(input.value);
  }

  componentDidMount() {
    this.disableTabbing();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { input } = nextProps;

    this.setFormattedValue(input.value);
  }
  componentDidUpdate() {
    this.disableTabbing();
  }

  setValue(value, cb = noop) {
    if (isObject(value)) {
      value = {
        min: Number(value.min),
        max: Number(value.max),
      };
    }

    // Handlers that run right after setValue read the value from here:
    // outside React event handlers the state update is batched and
    // this.state still holds the previous value.
    this.latestValue = value;
    this.setState({ value }, cb);
  }

  setFormattedValue(value) {
    this.setValue(this.formatValue(value));
  }

  // We need to disable keyboard tab actions for slider.
  disableTabbing() {
    const sliderEl = this.sliderRef.current;

    if (!sliderEl) return;

    const thumbs = sliderEl.querySelectorAll('[role="slider"]');

    thumbs.forEach((thumb) => {
      thumb.setAttribute('tabindex', '-1');
    });
  }

  prepareValue(mapping, value) {
    const { options, isRangeMode } = this.props;

    if (!options) {
      return value;
    }

    return isRangeMode
      ? {
          min: mapping[value.min],
          max: mapping[value.max],
        }
      : mapping[value];
  }

  formatValue(value) {
    const { indexesByValue } = this.props;

    return this.prepareValue(indexesByValue, value);
  }

  parseValue(value) {
    const { valuesByIndex } = this.props;

    return this.prepareValue(valuesByIndex, value);
  }

  handleChange = (value) => {
    const { minValue, maxValue, isRangeMode } = this.props;

    let valueToSet = value;

    if (isRangeMode) {
      let minValueToSet = value.min;

      let maxValueToSet = value.max;

      if (value.min < minValue) {
        minValueToSet = minValue;
      }

      if (value.max > maxValue) {
        maxValueToSet = maxValue;
      }

      valueToSet = { min: minValueToSet, max: maxValueToSet };
    }

    // react-input-range changes the value from requestAnimationFrame and
    // compares its value prop on mouseup, so the update has to be committed
    // before the next event arrives.
    flushSync(() => {
      this.setValue(valueToSet);
    });
  };

  handleInputControlChange = ({ min, max }) => {
    this.setValue({ min, max });
  };

  handleInputControlBlur = ({ min, max }, cb = noop) => {
    const { input } = this.props;

    this.setValue({ min, max }, () => {
      const { value } = this.state;

      input.onBlur(value);
      cb();
    });
  };

  handleChangeComplete = () => {
    const { onChanged, input } = this.props;

    input.onChange(this.parseValue(this.latestValue));

    onChanged();
  };

  handleSliderChangeComplete = () => {
    const { onSliderChange } = this.props;

    this.handleChangeComplete();
    onSliderChange(this.parseValue(this.latestValue));
  };

  handleInputControlChangeComplete = () => {
    const { onInputControlChange } = this.props;

    this.handleChangeComplete();
    onInputControlChange(this.parseValue(this.latestValue));
  };

  renderRangeLabel() {
    const { RangeLabel } = this.props;
    const { value } = this.state;

    return (
      <RangeLabel
        {...getOtherProps(this.props)}
        data-qa-selector-value={value}
        value={this.parseValue(value)}
      />
    );
  }

  renderInputControl() {
    const { InputControl, input, minValue, maxValue } = this.props;
    const { value } = this.state;

    return (
      <InputControl
        name={input.name}
        value={value}
        emptyValue={{ min: minValue, max: maxValue }}
        onBlur={this.handleInputControlBlur}
        onChange={this.handleInputControlChange}
        onChangeComplete={this.handleInputControlChangeComplete}
      />
    );
  }

  render() {
    const { className, input, inputRangeProps, minValue, maxValue } =
      this.props;
    const { value } = this.state;

    return (
      <div
        data-qa-selector="range-slider-control"
        className={cns(cn.root, className)}
      >
        <div className={cn.inputRangeSliderWrapper} ref={this.sliderRef}>
          <InputRange
            {...getOtherProps(this.props)}
            {...inputRangeProps}
            {...input}
            minValue={minValue}
            maxValue={maxValue}
            value={value}
            onChange={this.handleChange}
            onChangeComplete={this.handleSliderChangeComplete}
          />
        </div>
        {this.renderInputControl()}
        {this.renderRangeLabel()}
      </div>
    );
  }
}

export default mapProps(propsMapping)(RangeSliderControl);
